import {useState,useEffect,useCallback} from 'react';
import {Stack,Button,Typography,Menu,MenuItem,Chip} from '@mui/material';
import {useKeycloak} from "@react-keycloak/web";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import SickIcon from '@mui/icons-material/Sick';
import TimerIcon from '@mui/icons-material/Timer';
import {useApplication} from '../ApplicationContext';
import {useSnackbar} from '../SnackbarContext.js';
import {consoleLog} from '../../../core/utilities/Utilities.js';
import {getWorkdayStatus,workdayAction} from '../../integration/Api.js';
const file='WorkdayControls.js';

const STATUS_LABELS={
    NOT_STARTED:'WorkdayStatusNotStarted',
    RUNNING:'WorkdayStatusRunning',
    PAUSED:'WorkdayStatusPaused',
    STOPPED:'WorkdayStatusStopped',
};
const STATUS_COLORS={
    NOT_STARTED:'default',
    RUNNING:'success',
    PAUSED:'warning',
    STOPPED:'default',
};

const minutesToHHMM=(minutes)=>{
    const value=Math.max(0,Math.floor(minutes||0));
    return String(Math.floor(value/60)).padStart(2,'0')+':'+String(value%60).padStart(2,'0');
};

//Workday timer state of the logged user - shared by the Footer bar and the My workday page.
//Any action bumps globalData.workdayToken so every other mounted instance reloads too.
export const useWorkday=()=>{
    const {keycloak}=useKeycloak();
    const isLoggedIn=keycloak.authenticated;
    const {globalData,updateGlobalData}=useApplication();
    const {showSnackbar}=useSnackbar();
    const [status,setStatus]=useState(null);
    const [fetchedAt,setFetchedAt]=useState(Date.now());
    const [busy,setBusy]=useState(false);
    const workdayToken=globalData?.workdayToken;

    const refresh=useCallback(async()=>{
        const data=await getWorkdayStatus();
        consoleLog({file:file,context:'refresh - getWorkdayStatus',arg_name:'data',arg_value:data});
        if(data&&!data.error){
            setStatus(data);
            setFetchedAt(Date.now());
        }
    },[]);

    useEffect(()=>{
        if(isLoggedIn){
            refresh();
        }
        function handleVisibilityEvent(){
            if(document.visibilityState==='visible'&&isLoggedIn){
                refresh();
            }
        }
        document.addEventListener("visibilitychange",handleVisibilityEvent);
        return()=>{
            document.removeEventListener("visibilitychange",handleVisibilityEvent);
        };
    },[isLoggedIn,workdayToken,refresh]);

    const runAction=useCallback(async(action,body={})=>{
        setBusy(true);
        const data=await workdayAction(action,body);
        consoleLog({file:file,context:'runAction - workdayAction',arg_name:'data',arg_value:data});
        setBusy(false);
        if(!data||data.error){
            showSnackbar(data?.error,'error');
            refresh();
            return null;
        }
        setStatus(data);
        setFetchedAt(Date.now());
        updateGlobalData({...globalData,workdayToken:Date.now()});
        return data;
    },[globalData,updateGlobalData,showSnackbar,refresh]);

    return {status,fetchedAt,busy,refresh,runAction};
};

//Today's worked time, ticking locally while an entry is running
export const WorkdayElapsed=({status,fetchedAt,variant='body1',sx})=>{
    const [now,setNow]=useState(Date.now());
    const running=status?.status==='RUNNING';

    useEffect(()=>{
        if(!running) return;
        const interval=setInterval(()=>setNow(Date.now()),15000);
        return()=>clearInterval(interval);
    },[running]);

    useEffect(()=>{
        setNow(Date.now());
    },[fetchedAt]);

    let minutes=status?.worked_minutes_closed||0;
    if(running){
        minutes+=Math.floor(((status?.running_elapsed_seconds||0)+Math.max(0,(now-fetchedAt)/1000))/60);
    }
    return <Typography variant={variant} sx={{fontVariantNumeric:'tabular-nums',...sx}}>{minutesToHHMM(minutes)}</Typography>;
};

const WorkdayControls=({workday,application,compact=false,onSick})=>{
    const {status,fetchedAt,busy,runAction}=workday;
    const [anchorEl,setAnchorEl]=useState(null);
    const getLocale=(key)=>application?.getLocale(key);

    if(!status?.enabled) return null;

    const size=compact?'small':'medium';
    const buttonSx=compact?{py:0,px:1,minWidth:0,fontSize:'0.75rem'}:{};
    const categories=status.categories||[];

    const handleStart=(category)=>{
        setAnchorEl(null);
        runAction('start',{category:category});
    };

    return (
        <Stack direction="row" spacing={compact?1:2} alignItems="center" justifyContent="center" flexWrap="wrap" useFlexGap>
            <TimerIcon fontSize={compact?'small':'medium'}/>
            <Chip size="small" color={STATUS_COLORS[status.status]} label={getLocale(STATUS_LABELS[status.status])+(status.current_category_label?(' · '+status.current_category_label):'')}/>
            <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant={compact?'caption':'body1'}>{getLocale('WorkdayToday')+':'}</Typography>
                <WorkdayElapsed status={status} fetchedAt={fetchedAt} variant={compact?'caption':'body1'} sx={{fontWeight:'bold'}}/>
            </Stack>
            {status.status==='RUNNING'&&
            <>
                <Button size={size} sx={buttonSx} variant="contained" color="warning" startIcon={<PauseIcon/>} disabled={busy} onClick={()=>runAction('pause')}>{getLocale('WorkdayPause')}</Button>
                <Button size={size} sx={buttonSx} variant="contained" color="error" startIcon={<StopIcon/>} disabled={busy} onClick={()=>runAction('stop')}>{getLocale('WorkdayStop')}</Button>
            </>}
            {status.status==='PAUSED'&&
                <Button size={size} sx={buttonSx} variant="contained" color="success" startIcon={<PlayArrowIcon/>} disabled={busy} onClick={()=>runAction('resume')}>{getLocale('WorkdayResume')}</Button>}
            {status.status!=='RUNNING'&&
            <>
                <Button size={size} sx={buttonSx} variant={status.status==='PAUSED'?'outlined':'contained'} color="success" startIcon={<PlayArrowIcon/>} disabled={busy} onClick={(e)=>setAnchorEl(e.currentTarget)}>{getLocale('WorkdayStart')}</Button>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={()=>setAnchorEl(null)}>
                    <MenuItem disabled dense>{getLocale('WorkdaySelectCategory')}</MenuItem>
                    {categories.map((category)=>(
                        <MenuItem key={category.name} onClick={()=>handleStart(category.name)}>{category.value}</MenuItem>
                    ))}
                </Menu>
            </>}
            {onSick&&
                <Button size={size} sx={buttonSx} variant="outlined" color="secondary" startIcon={<SickIcon/>} disabled={busy} onClick={onSick}>{getLocale('WorkdaySick')}</Button>}
        </Stack>
    );
};

export default WorkdayControls;
